import { useState, useEffect } from "react";
import PageMeta from "../../components/common/PageMeta";
import DateRangePicker from "../../components/daterange/DateRangePicker";
import MultiSelectCheckbox, { Option as MultiOption } from "../../components/form/MultiSelectCheckbox";
import GroupedSelect, { Product, SelectOption } from "../../components/form/GroupedSelect";
import DropdownButton from "../../components/ui/button/DropdownButton";
import { FileDown } from "lucide-react";
import { exportToCSV, exportToXLS, formatSubscriptionsForExport } from "../../utils/exportUtils";
import { SUBSCRIPTION_STATUS_OPTIONS } from "../../utils/subscriptionStatus";
import api from "../../services/api";
import SubscriptionsMetrics from "../../components/subscriptions/SubscriptionsMetrics";
import SubscriptionsTable from "../../components/subscriptions/SubscriptionsTable";

// Interface para o tipo de dados da API
interface SubscriptionsData {
    summary: {
        totalSubscriptions: number;
        activeSubscriptions: number;
        canceledSubscriptions: number;
        pausedSubscriptions: number;
        pastDueSubscriptions: number;
        mrrEstimated: number;
    };
    subscriptionsList: Array<any>;
    pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
    };
}

// Teto da exportação. O backend não limita o `limit`, e um período largo pode
// trazer milhares de linhas numa só resposta — isto evita derrubar a aba do
// produtor e avisa quando o arquivo não veio completo.
const EXPORT_MAX_ROWS = 5000;

export default function MySubscriptions() {
    // Estados para pesquisa e filtro
    const [searchTermProdutos, setSearchTermProdutos] = useState("");
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [paymentMethodFilters, setPaymentMethodFilters] = useState<string[]>([]);
    const [productOfferFilter, setProductOfferFilter] = useState<SelectOption | null>(null);

    // Estados para produtos e ofertas
    const [products, setProducts] = useState<Product[]>([]);

    // Estados para os dados da API
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [subscriptionsData, setSubscriptionsData] = useState<SubscriptionsData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);

    // Estados para paginação (controlada pelo backend)
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(20);

    // Estado para o termo de busca com debounce (enviado para API)
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

    // Carregar datas do localStorage (se existirem) ou definir padrão
    const getSavedDateRange = () => {
        try {
            const savedRange = localStorage.getItem('dateRangeSelection');
            if (savedRange) {
                const { startDate, endDate } = JSON.parse(savedRange);
                return {
                    startDate: startDate ? new Date(startDate) : null,
                    endDate: endDate ? new Date(endDate) : null
                };
            }
        } catch (error) {
            console.error("Erro ao recuperar datas do localStorage:", error);
        }

        // Se não houver dados salvos ou houver erro, definir hoje
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return {
            startDate: today,
            endDate: today
        };
    };

    // Estado para armazenar as datas selecionadas
    const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(getSavedDateRange());

    // Opções de status: vêm de src/utils/subscriptionStatus.ts, que lista só o que
    // o backend realmente grava (active, past_due, canceled, canceling).
    // Antes havia "Suspensa", "Pendente" e "Expirada", que NÃO EXISTEM no banco:
    // três das cinco opções nunca retornavam nada. E "Renovação pendente"
    // (past_due) não era oferecida, que era justamente a que o produtor procurava.
    const statusMultiOptions: MultiOption[] = SUBSCRIPTION_STATUS_OPTIONS.map(o => ({
        id: o.id,
        label: o.label,
    }));

    // Opções para o multi-select de método de pagamento (sem "Todos")
    const paymentMethodMultiOptions: MultiOption[] = [
        { id: "card", label: "Cartão" },
        { id: "pix", label: "PIX" },
        { id: "boleto", label: "Boleto" }
    ];

    // Função para buscar os produtos e suas ofertas
    const fetchProductsAndOffers = async () => {
        try {
            // Chamar a API para buscar produtos com suas ofertas
            const response = await api.get('/api/products/with-offers');

            if (response.data && response.data.status === 'success') {
                setProducts(response.data.data || []);
            } else {
                console.error("Erro ao carregar produtos:", response.data);
                setProducts([]);
            }
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
            setProducts([]);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // MONTAGEM DOS FILTROS — UMA FONTE SÓ
    //
    // A tabela e a exportação precisam enviar exatamente os mesmos filtros. Antes
    // a exportação refiltrava no cliente, por campos que não existem na resposta
    // (item.subscription?.status, quando a linha traz `status` na raiz): com
    // qualquer filtro de status marcado o resultado zerava e a tela dizia
    // "Não há dados para exportar".
    //
    // Agora quem filtra é o backend, nos dois casos, com estes mesmos parâmetros.
    // ═══════════════════════════════════════════════════════════════════════════
    const buildSubscriptionParams = (
        startDate: Date | null | undefined,
        endDate: Date | null | undefined,
        page: number,
        limit: number
    ) => {
        const params = new URLSearchParams();

        if (startDate && endDate) {
            // Formata a data no fuso de Brasília (GMT-3)
            const formatDateBR = (date: Date, endOfDay = false) => {
                const dateCopy = new Date(date);
                if (endOfDay) {
                    dateCopy.setHours(23, 59, 59, 999);
                } else {
                    dateCopy.setHours(0, 0, 0, 0);
                }
                return dateCopy.toLocaleString('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                }).replace(/(\d+)\/(\d+)\/(\d+).*?(\d+):(\d+):(\d+)/, '$3-$2-$1T$4:$5:$6-03:00');
            };

            params.append('startDate', formatDateBR(startDate));
            params.append('endDate', formatDateBR(endDate, true));
        }

        if (productOfferFilter) {
            if (productOfferFilter.isChildOption) {
                params.append('offerId', productOfferFilter.value);
            } else {
                params.append('productId', productOfferFilter.value);
            }
        }

        paymentMethodFilters?.forEach((pm) => params.append('paymentMethod', pm));
        statusFilters?.forEach((st) => params.append('status', st));

        params.append('page', String(page));
        params.append('limit', String(limit));

        if (debouncedSearchTerm && debouncedSearchTerm.trim() !== "") {
            params.append('search', debouncedSearchTerm.trim());
        }

        return params;
    };

    // Função para exportar as assinaturas em CSV ou XLS
    const handleExport = async (format: string) => {
        if (isExporting) return;

        const total = subscriptionsData?.pagination?.total ?? 0;
        if (total === 0) {
            alert("Não há dados para exportar.");
            return;
        }

        try {
            setIsExporting(true);

            // A exportação precisa levar TODOS os registros do filtro, não só a
            // página aberta: antes exportava apenas os 20 itens de subscriptionsList
            // e o produtor recebia um arquivo truncado sem perceber.
            const limite = Math.min(total, EXPORT_MAX_ROWS);
            const params = buildSubscriptionParams(dateRange.startDate, dateRange.endDate, 1, limite);
            const response = await api.get(`/api/subscriptions?${params.toString()}`);

            if (response.data?.status !== 'success') {
                throw new Error('resposta inesperada da API');
            }

            const lista = response.data.data?.subscriptionsList ?? [];
            if (lista.length === 0) {
                alert("Não há dados para exportar.");
                return;
            }

            const formattedData = formatSubscriptionsForExport(lista);
            const dateStr = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
            const fileName = `assinaturas_${dateStr}`;

            if (format === 'csv') {
                exportToCSV(formattedData, fileName);
            } else if (format === 'xlsx') {
                exportToXLS(formattedData, fileName);
            }

            if (total > EXPORT_MAX_ROWS) {
                alert(
                    `A exportação traz as primeiras ${EXPORT_MAX_ROWS.toLocaleString('pt-BR')} assinaturas ` +
                    `de ${total.toLocaleString('pt-BR')}. Use um período menor ou filtros para exportar o resto.`
                );
            }
        } catch (error) {
            console.error("Erro ao exportar assinaturas:", error);
            alert("Não foi possível exportar as assinaturas. Tente novamente.");
        } finally {
            setIsExporting(false);
        }
    };

    // Função para buscar os dados de assinaturas com filtros
    const fetchSubscriptionsData = async (startDate?: Date | null, endDate?: Date | null) => {
        try {
            setIsLoading(true);
            setError(null);

            // Mesmos filtros da exportação — ver buildSubscriptionParams acima
            let url = '/api/subscriptions';
            const params = buildSubscriptionParams(startDate, endDate, currentPage, itemsPerPage);

            // Adicionar os parâmetros à URL se houver algum
            if (params.toString()) {
                url = `${url}?${params.toString()}`;
            }

            // Chamada para a API
            const response = await api.get(url);

            if (response.data && response.data.status === 'success') {
                setSubscriptionsData(response.data.data);
            } else {
                console.error("Erro ao carregar dados de assinaturas:", response.data);
                setError("Erro ao carregar os dados de assinaturas");
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            setError("Erro ao carregar os dados. Tente novamente.");
        } finally {
            setIsLoading(false);
        }
    };

    // Efeito para carregar os produtos quando o componente é montado
    useEffect(() => {
        fetchProductsAndOffers();
    }, []);

    // Debounce para o termo de busca (500ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTermProdutos);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTermProdutos]);

    // Efeito para carregar os dados de assinaturas quando os filtros mudam
    useEffect(() => {
        // Garantir que temos datas válidas para buscar
        if (dateRange.startDate && dateRange.endDate) {
            fetchSubscriptionsData(dateRange.startDate, dateRange.endDate);
        }
    }, [dateRange, productOfferFilter, paymentMethodFilters, statusFilters, currentPage, debouncedSearchTerm]); // Recarregar quando os filtros, página ou busca mudam

    // Resetar para página 1 quando filtros mudam (exceto a própria página)
    useEffect(() => {
        setCurrentPage(1);
    }, [dateRange, productOfferFilter, paymentMethodFilters, statusFilters, debouncedSearchTerm]);

    // Função para mudar de página
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Função para quando o período de datas mudar
    const handleDateRangeChange = (range: { startDate: Date | null; endDate: Date | null }) => {
        // Armazenar o novo intervalo de datas selecionado
        setDateRange({
            startDate: range.startDate,
            endDate: range.endDate
        });

        // Não precisamos chamar fetchSubscriptionsData aqui, pois o useEffect acima será acionado
        // quando dateRange mudar e fará a chamada para nós
        console.log("Intervalo de datas atualizado:", range);
    };

    return (
        <>
            <PageMeta
                title="Sellyo - Assinaturas"
                description="Minhas Assinaturas"
            />
            <div className="grid grid-cols-12 gap-4 md:gap-6">
                <div className="col-span-12">
                    <div className="flex justify-between items-center">
                        <div className="w-auto">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                                Assinaturas
                            </h3>
                        </div>
                        <div className="w-auto">
                            <DateRangePicker
                                onChange={handleDateRangeChange}
                                initialStartDate={dateRange.startDate}
                                initialEndDate={dateRange.endDate}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-span-12 space-y-6">
                    {error && (
                        <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800">
                            <span className="font-medium">Erro:</span> {error}
                        </div>
                    )}
                    <SubscriptionsMetrics
                        summary={subscriptionsData?.summary}
                        isLoading={isLoading}
                    />
                    {/* Filtros acima da tabela */}
                    <div className="mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Campo de busca */}
                            <div className="relative flex-1">
                                <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                    <svg
                                        className="fill-gray-500 dark:fill-gray-400"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                            fill=""
                                        />
                                    </svg>
                                </button>
                                <input
                                    type="text"
                                    placeholder="Pesquisar transação..."
                                    value={searchTermProdutos}
                                    onChange={(e) => setSearchTermProdutos(e.target.value)}
                                    className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                                />
                            </div>

                            {/* Select para Produto / Oferta */}
                            <div className="w-full md:w-64">
                                <GroupedSelect
                                    products={products}
                                    placeholder="Produto / Oferta"
                                    onChange={(option) => setProductOfferFilter(option)}
                                    className="dark:bg-dark-900 h-11"
                                    value={productOfferFilter}
                                />
                            </div>

                            {/* Multi-select para método de pagamento */}
                            <div className="w-full md:w-64">
                                <MultiSelectCheckbox
                                    options={paymentMethodMultiOptions}
                                    selectedValues={paymentMethodFilters}
                                    onChange={(vals) => setPaymentMethodFilters(vals)}
                                    placeholder="Método de pagamento"
                                    showSelectAll={false}
                                    searchable={false}
                                />
                            </div>

                            {/* Multi-select para status */}
                            <div className="w-full md:w-64">
                                <MultiSelectCheckbox
                                    options={statusMultiOptions}
                                    selectedValues={statusFilters}
                                    onChange={(vals) => setStatusFilters(vals)}
                                    placeholder="Filtrar por status"
                                    showSelectAll={false}
                                    searchable={false}
                                />
                            </div>

                            <div className="w-auto">
                                <DropdownButton
                                    size="sm"
                                    variant="primary"
                                    disabled={isExporting || isLoading}
                                    startIcon={<FileDown className="size-5" />}
                                    options={[
                                        {
                                            label: "Exportar como CSV",
                                            value: "csv",
                                            onClick: () => handleExport("csv")
                                        },
                                        {
                                            label: "Exportar como XLS",
                                            value: "xls",
                                            onClick: () => handleExport("xlsx")
                                        }
                                    ]}
                                >
                                    {isExporting ? "Exportando..." : "Exportar"}
                                </DropdownButton>
                            </div>
                        </div>
                    </div>
                    <SubscriptionsTable
                        subscriptionsList={subscriptionsData?.subscriptionsList || []}
                        isLoading={isLoading}
                        // Paginação do backend
                        currentPage={currentPage}
                        totalPages={subscriptionsData?.pagination?.pages || 1}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </>
    );
}